import type {
  CellViewWire,
  ContactViewWire,
  OrderTargetWire,
  Rung,
} from '@badger-sentinel/protocol';
import { RUNGS, type ConsoleTab } from '@badger-sentinel/rules';

import {
  ACCESS_UNKNOWN,
  HARDENING_UNKNOWN,
  ROUTER_UNKNOWN,
  accessRung,
  networkRows,
} from './cyber.js';
import { missionLabel } from './missions.js';
import {
  creditAt,
  observableLabel,
  observationFor,
  signed,
  trackStateOf,
  type MwTrackWire,
} from './mw.js';
import type { QueuedOrder } from './queue.js';
import type {
  CyberContactViewWire,
  CyberNetworkViewWire,
  FriendlyAssetViewWire,
  RegionViewWire,
  StatusEffectViewWire,
} from './view-types.js';

/**
 * The selection, resolved against a view (design 1152–1605).
 *
 * One store key, three subject kinds, and both rails read it. Selection lives in
 * the Zustand store rather than in the view because `CellView` is replaced
 * wholesale on every resolution (M5-D9) — a rail that lost its selection on
 * every turn advance would be unusable by turn three. So the store holds a
 * `{kind, id}` pair and *this* module resolves it against whatever snapshot is
 * current. A selection whose subject has left the view resolves to `MISSING`,
 * which is a real state and not an empty panel.
 */

export type SelectionKind = 'ASSET' | 'CONTACT' | 'REGION' | 'BAND' | 'NETWORK' | 'MW_TRACK';

export interface Selection {
  readonly kind: SelectionKind;
  readonly id: string;
}

export type ResolvedSelection =
  | { readonly kind: 'NONE' }
  | { readonly kind: 'MISSING'; readonly ref: Selection }
  | { readonly kind: 'ASSET'; readonly asset: FriendlyAssetViewWire }
  | { readonly kind: 'CONTACT'; readonly contact: ContactViewWire }
  /**
   * Phase 7's other resolution — a machine known **only** to cyber.
   *
   * Not a seventh `Selection` kind, and that distinction is the point: the
   * selection is still `{kind: 'CONTACT', id}`, because what the seat has hold of
   * is an alias for something it was never told the identity of, which is exactly
   * what a contact is. What differs is *where the alias resolves*: RB §5.6.2's
   * parenthesis keeps a foothold-revealed machine out of `contacts[]` — "visible
   * (to cyber; not FOUND)" — so a board contact and a cyber contact are two
   * different rows carrying the same name.
   *
   * Without this arm, clicking half of what the cyber board draws would put
   * `NOT IN THIS PICTURE` in the rail about a card the operator is looking at,
   * which is the failure Phase 6 fixed for orbital bands.
   */
  | { readonly kind: 'CYBER_CONTACT'; readonly contact: CyberContactViewWire }
  | { readonly kind: 'REGION'; readonly region: RegionViewWire }
  /**
   * Phase 6's fifth subject — an orbital band, which is a zone like any other.
   *
   * A ring on the SDA canvas reports itself through `bs-region` carrying a
   * **band** id rather than a region one, and the two vocabularies must not
   * merge: `map.regions` has no `GEO` in it, so a band arriving as a `REGION`
   * selection resolves to `MISSING` and the rail says `NOT IN THIS PICTURE`
   * about a ring the operator is looking at. Its own kind, resolved against
   * `map.bandSlots`, which is where a band is declared.
   */
  | { readonly kind: 'BAND'; readonly band: string; readonly slots: number }
  /**
   * Phase 7's sixth subject — a cyber enclave, which is a zone the ground has no
   * name for in exactly the way a ring is.
   *
   * The same argument Phase 6 made for `BAND`, held against a third canvas:
   * `map.regions` has no `BLUE-CORE` in it, so an enclave arriving as a `REGION`
   * selection resolves to `MISSING` and the rail says `NOT IN THIS PICTURE`
   * about a segment the operator has just clicked. Resolved against
   * `cyber.networks`, which is where an enclave is declared and — the part that
   * matters — where the projection decides an enclave may be *seen at all*. An
   * enemy network the viewer has never reached is absent from that list, so a
   * stale selection of one resolves to `MISSING`, which is the truth.
   *
   * It also closes Phase 2's hole: `roster.ts` has had a `NETWORK` row subject
   * since the rails were built and had nothing to select, so a network row
   * selected its *router* when the router happened to be one of ours and nothing
   * at all otherwise.
   */
  | { readonly kind: 'NETWORK'; readonly network: CyberNetworkViewWire }
  /**
   * Phase 4's fourth subject — the ledger's second row, collected.
   *
   * A launch event is a selection like any other: it survives a snapshot swap
   * because the store holds `{kind, id}` and this module resolves it, and a track
   * that has left the board says `NOT IN THIS PICTURE` rather than emptying the
   * rail. `DetailBlock` needs no change to draw it, which is the whole reason it
   * was built to take rows.
   */
  | { readonly kind: 'MW_TRACK'; readonly track: MwTrackWire };

/**
 * The kind of thing a bare id names, decided against the view.
 *
 * The **caller never says** — which is the one place a contact could acquire an
 * asset's treatment. Lifted out of `ConsoleShell`'s `selectionForMenu` when the
 * intel reader became its second caller: an entry names an actor and a contact
 * as plain strings, and asking "which of these four lists is it in" in two files
 * is the shape of the bug Phase 6.5 shipped.
 *
 * Returns null for an id in none of them — which for a report is a *normal*
 * answer rather than a fault, because a report is about a resolved turn and the
 * asset it names may since have been destroyed.
 */
export function selectionForId(view: CellViewWire | null, id: string): Selection | null {
  if (view === null) return null;
  if (view.contacts.some((contact) => contact.id === id)) return { kind: 'CONTACT', id };
  if (view.friendly.some((asset) => asset.id === id)) return { kind: 'ASSET', id };
  // A machine revealed to cyber is still a contact — an alias for something the
  // seat was never told the identity of. It is simply not in `contacts[]`.
  if (view.cyber.contacts.some((contact) => contact.alias === id)) return { kind: 'CONTACT', id };
  if (view.cyber.networks.some((network) => network.id === id)) return { kind: 'NETWORK', id };
  return null;
}

export function resolveSelection(
  view: CellViewWire | null,
  selection: Selection | null,
): ResolvedSelection {
  if (selection === null) return { kind: 'NONE' };
  if (view === null) return { kind: 'MISSING', ref: selection };

  if (selection.kind === 'ASSET') {
    const asset = view.friendly.find((candidate) => candidate.id === selection.id);
    return asset === undefined ? { kind: 'MISSING', ref: selection } : { kind: 'ASSET', asset };
  }
  if (selection.kind === 'CONTACT') {
    const contact = view.contacts.find((candidate) => candidate.id === selection.id);
    if (contact !== undefined) return { kind: 'CONTACT', contact };
    // The detection picture first, the cyber picture second, and never both: an
    // alias in `contacts[]` is a machine something *looked at*, and the panel it
    // gets says rung, location and signature. The cyber row is the thinner
    // answer for a machine nothing has looked at, and it is only reached when the
    // richer one is absent.
    const cyber = view.cyber.contacts.find((candidate) => candidate.alias === selection.id);
    return cyber === undefined
      ? { kind: 'MISSING', ref: selection }
      : { kind: 'CYBER_CONTACT', contact: cyber };
  }
  if (selection.kind === 'BAND') {
    const slots = (view.map.bandSlots as Readonly<Record<string, number>>)[selection.id];
    return slots === undefined
      ? { kind: 'MISSING', ref: selection }
      : { kind: 'BAND', band: selection.id, slots };
  }
  if (selection.kind === 'NETWORK') {
    const network = view.cyber.networks.find((candidate) => candidate.id === selection.id);
    return network === undefined
      ? { kind: 'MISSING', ref: selection }
      : { kind: 'NETWORK', network };
  }
  if (selection.kind === 'MW_TRACK') {
    const track = view.mwTracks.find((candidate) => candidate.eventId === selection.id);
    return track === undefined ? { kind: 'MISSING', ref: selection } : { kind: 'MW_TRACK', track };
  }
  const region = view.map.regions.find((candidate) => candidate.id === selection.id);
  return region === undefined ? { kind: 'MISSING', ref: selection } : { kind: 'REGION', region };
}

// ---------------------------------------------------------------------------
// The detection ladder
// ---------------------------------------------------------------------------

/**
 * Four rungs, of which a contact can only ever be in three (Q5).
 *
 * `RUNGS` is `HIDDEN · FOUND · FIXED · TRACKED` and `TRACKED` is the top —
 * `DETECT` is deleted. But `zContactView.rung` is `FOUND | FIXED | TRACKED`,
 * with the schema's own comment saying why: *"never HIDDEN — a hidden contact is
 * absent from the projection entirely"*.
 *
 * So HIDDEN is drawn as the **floor a contact has already climbed off**, never
 * as a state a row is in. It is on the ladder because a ladder that starts at
 * FOUND does not say what FOUND is better than, and it is drawn `PASSED` for
 * every contact that exists — which is all of them.
 */
export type LadderRungState = 'PASSED' | 'CURRENT' | 'AHEAD';

export interface LadderStep {
  readonly rung: Rung;
  readonly state: LadderRungState;
}

export function detectionLadder(current: ContactViewWire['rung']): readonly LadderStep[] {
  const reached = RUNGS.indexOf(current);
  return RUNGS.map((rung, index) => ({
    rung,
    state: index < reached ? 'PASSED' : index === reached ? 'CURRENT' : 'AHEAD',
  }));
}

// ---------------------------------------------------------------------------
// Environment
// ---------------------------------------------------------------------------

/**
 * Weather now and next, for one region.
 *
 * **`theatre.forecast` is nullable and null means unknown**, never "same as
 * now" (must-not #3). The three states are distinguishable by construction:
 *
 * | `forecast` | `next` | drawn as |
 * | --- | --- | --- |
 * | `null` | `{ known: false }` | `UNKNOWN`, no glyph |
 * | present, same as now | `null` | nothing — the second row is absent |
 * | present, different | `{ known: true, state }` | the next glyph and its word |
 *
 * A missing region key inside a present `forecast` is also unknown: the record
 * is per-region and the theatre-wide null is only the commonest way for a value
 * to be absent.
 */
export interface WeatherOutlook {
  readonly now: string | null;
  readonly next:
    { readonly known: false } | { readonly known: true; readonly state: string } | null;
}

export function weatherFor(view: CellViewWire, region: string): WeatherOutlook {
  const now = view.theatre.weather[region] ?? null;
  if (view.theatre.forecast === null) return { now, next: { known: false } };
  const forecast = view.theatre.forecast[region];
  if (forecast === undefined) return { now, next: { known: false } };
  return { now, next: forecast === now ? null : { known: true, state: forecast } };
}

// ---------------------------------------------------------------------------
// Range
// ---------------------------------------------------------------------------

/**
 * The declared range from one origin to everywhere else (Q3, region-map.md).
 *
 * > Range is declared per pair and **obeys nothing**: A–B may be 3, B–C 3, and
 * > A–C 20. It is not a metric, not additive, and not derivable from the graph.
 *
 * Two consequences, both enforced here rather than left to the component:
 *
 * 1. **It appears only from a selected origin** (must-not #6). There is no
 *    function in this module that returns a range without being handed one, and
 *    `map.ranges` is a symmetric pair list that nothing else reads.
 * 2. **Adjacency is not range.** `adjacentTo` is load-bearing for exactly one
 *    rule (RB §5.8.2's "one region off") and is returned by
 *    `adjacencyOf` — separately, with no number attached, so no component can
 *    accidentally render one as the other. The Dijkstra solver the mockup used
 *    was deleted in Q3 and nothing here reconstructs it.
 */
export interface RangeEntry {
  readonly region: string;
  readonly name: string;
  readonly range: number;
}

export function rangesFrom(view: CellViewWire, origin: string): readonly RangeEntry[] {
  const names = new Map(view.map.regions.map((region) => [region.id, region.name]));
  return view.map.ranges
    .flatMap((pair) => {
      if (pair.from === origin) return [{ region: pair.to, range: pair.range }];
      if (pair.to === origin) return [{ region: pair.from, range: pair.range }];
      return [];
    })
    .map((entry) => ({ ...entry, name: names.get(entry.region) ?? entry.region }))
    .sort((a, b) => a.range - b.range || a.region.localeCompare(b.region));
}

/** Region ids one step away. **Not** a range, and never drawn with a number. */
export function adjacencyOf(view: CellViewWire, region: string): readonly string[] {
  return view.map.regions.find((candidate) => candidate.id === region)?.adjacentTo ?? [];
}

/**
 * The origin a selection offers, or null.
 *
 * A region is its own origin; a ground asset's is the region it stands in. An
 * asset in orbit, in transit or off the map has none — and an orbital asset that
 * silently borrowed a region would be exactly the invented range the rule
 * forbids.
 */
export function rangeOriginOf(resolved: ResolvedSelection): string | null {
  if (resolved.kind === 'REGION') return resolved.region.id;
  if (resolved.kind === 'ASSET' && resolved.asset.location.kind === 'GROUND') {
    return resolved.asset.location.region;
  }
  if (resolved.kind === 'CONTACT' && resolved.contact.location.kind === 'REGION') {
    return resolved.contact.location.region;
  }
  return null;
}

/**
 * Why the selected thing is not drawn on the board you are looking at, and where
 * it is instead.
 *
 * About half of every seat's order of battle is in orbit, and selecting one of
 * those used to leave the canvas byte-identical — no highlight, no dim, no note.
 * The chip filled, the rail filled, the map said nothing, so *in orbit, ask SDA*,
 * *I mis-clicked* and *the map is broken* were one picture. This is the same
 * refusal `weatherFor` and `rangeOriginOf` already make: an absent value is
 * rendered as absent, with the reason, rather than as nothing.
 *
 * ## It is a property of the selection, not of one tab
 *
 * This answered only the theatre's version of the question — *is it in orbit?* —
 * so the orbital board, which has the identical dead end pointing the other way,
 * got silence. Five of the twelve contacts a BLUFOR seat holds in turn 1 are on
 * the ground, and clicking one with `SPACE SDA` open filled the chip and the rail
 * and left the board unchanged. So the caller now says which board is asking.
 *
 * **What counts as drawn on the orbital board is the board, not the rings.** A
 * `TRANSIT` asset, a `BAND` contact and an `UNKNOWN` contact carry no slot and
 * cannot be a marker (`model/orbital.ts`), but every one of them is stated in the
 * band strip an inch below — so from `SDA` they are present and this returns
 * `null`. Only a ground asset and a region contact are genuinely elsewhere.
 *
 * `null` means the selection **is** on this board, or there is nothing selected —
 * both cases where the board answers for itself.
 */
export interface OffMapNotice {
  /** The id the operator just clicked, echoed so the strip names its subject. */
  readonly id: string;
  /** Where it actually is: `GEO`, `LEO SLOT 13`, `LEO → GEO`, `OFF MAP`, `UNLOCATED`. */
  readonly where: string;
  /** The tab that does draw it, or null when no tab does. */
  readonly tab: ConsoleTab | null;
}

export function offMapNoticeOf(
  resolved: ResolvedSelection | null,
  /** The board asking. A subject this board draws gets no notice. */
  from: ConsoleTab,
): OffMapNotice | null {
  if (resolved === null) return null;

  if (resolved.kind === 'ASSET') {
    const at = resolved.asset.location;
    if (at.kind === 'GROUND') {
      return from === 'THEATRE'
        ? null
        : { id: resolved.asset.id, where: at.region, tab: 'THEATRE' };
    }
    if (at.kind === 'ORBIT') {
      return from === 'SDA'
        ? null
        : { id: resolved.asset.id, where: at.band + ' SLOT ' + String(at.slot), tab: 'SDA' };
    }
    if (at.kind === 'TRANSIT') {
      return from === 'SDA'
        ? null
        : { id: resolved.asset.id, where: at.from + ' → ' + at.to, tab: 'SDA' };
    }
    // OFFMAP is the one location no board draws, so it is stated wherever you are.
    return { id: resolved.asset.id, where: 'OFF MAP', tab: null };
  }

  if (resolved.kind === 'CONTACT') {
    const at = resolved.contact.location;
    if (at.kind === 'REGION') {
      return from === 'THEATRE'
        ? null
        : { id: resolved.contact.id, where: at.region, tab: 'THEATRE' };
    }
    if (at.kind === 'BAND') {
      return from === 'SDA' ? null : { id: resolved.contact.id, where: at.band, tab: 'SDA' };
    }
    if (at.kind === 'SLOT') {
      return from === 'SDA'
        ? null
        : { id: resolved.contact.id, where: at.band + ' SLOT ' + String(at.slot), tab: 'SDA' };
    }
    // UNKNOWN is a located-nowhere contact, not a missing one. The orbital board
    // gives it a column of its own, so from there it is present; from anywhere
    // else that column is where it is.
    return from === 'SDA' ? null : { id: resolved.contact.id, where: 'UNLOCATED', tab: 'SDA' };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Detail rows
// ---------------------------------------------------------------------------

export interface DetailRow {
  readonly key: string;
  readonly value: string;
  /** Draws the value in the alert colour — a real condition, not decoration. */
  readonly tone?: 'warn' | 'red';
}

/**
 * A status effect, rendered.
 *
 * `StatusEffectView.cause` is a plain string **on purpose**: the schema says so,
 * "so that a client cannot enumerate the source vocabulary out of the schema it
 * was shipped". So it is rendered verbatim. There is no lookup table keyed by
 * cause anywhere in this codebase, because a lookup table keyed by cause is
 * exactly the enumeration the wire refuses to hand over — and `'UNKNOWN'`
 * therefore renders as `UNKNOWN` by the same path every other value takes, which
 * is the strongest possible form of must-not #4.
 */
export function effectRow(effect: StatusEffectViewWire, index: number): DetailRow {
  const attribution = effect.attributedTo === null ? '' : ' · ' + effect.attributedTo;
  const expiry = effect.permanent
    ? ' · PERMANENT'
    : effect.expiresAtTurn === null
      ? ''
      : ' · TO T' + String(effect.expiresAtTurn);
  return {
    key: 'EFFECT ' + String(index + 1),
    value: effect.severity + ' · ' + effect.cause + attribution + expiry,
    tone: effect.severity === 'OFFLINE' ? 'red' : 'warn',
  };
}

function locationWords(asset: FriendlyAssetViewWire): string {
  switch (asset.location.kind) {
    case 'ORBIT':
      return asset.location.band + ' SLOT ' + String(asset.location.slot);
    case 'TRANSIT':
      return (
        asset.location.from +
        ' → ' +
        asset.location.to +
        ' SLOT ' +
        String(asset.location.destSlot) +
        ' · ARRIVES T' +
        String(asset.location.arrivesTurn)
      );
    case 'GROUND':
      return asset.location.region;
    case 'OFFMAP':
      return 'OFF MAP';
  }
}

function contactLocationWords(contact: ContactViewWire): string {
  switch (contact.location.kind) {
    case 'BAND':
      return contact.location.band + ' · BAND ONLY';
    case 'SLOT':
      return contact.location.band + ' SLOT ' + String(contact.location.slot);
    case 'REGION':
      return contact.location.region;
    case 'UNKNOWN':
      return 'UNKNOWN';
  }
}

/** The key/value block for a friendly asset (design 1314–1348). */
export function assetRows(asset: FriendlyAssetViewWire): readonly DetailRow[] {
  const rows: DetailRow[] = [
    { key: 'TYPE', value: asset.typeId },
    { key: 'CELL', value: asset.assignedCell },
    { key: 'LOCATION', value: locationWords(asset) },
    { key: 'SEGMENT', value: asset.segment },
    { key: 'SIGNATURE', value: asset.signature },
    { key: 'HARDENING', value: asset.hardening },
    { key: 'NETWORK', value: asset.network },
    {
      key: 'CAPACITY',
      value: String(asset.missionsThisTurn.length) + ' / ' + String(asset.capacity),
    },
  ];
  if (asset.deltaV !== null) rows.push({ key: 'DELTA-V', value: asset.deltaV.toFixed(1) });
  if (asset.magazine !== null) rows.push({ key: 'MAGAZINE', value: String(asset.magazine) });
  if (asset.mobility !== null) rows.push({ key: 'MOBILITY', value: asset.mobility });
  if (asset.ttcStation !== null) rows.push({ key: 'TT&C', value: asset.ttcStation });
  if (asset.emcon) rows.push({ key: 'EMCON', value: 'SET', tone: 'warn' });
  if (!asset.controllable) {
    rows.push({ key: 'CONTROL', value: asset.controlLoss ?? 'LOST', tone: 'red' });
  }
  if (asset.relocate !== null) {
    rows.push({
      key: 'RELOCATING',
      value: asset.relocate.toRegion + ' · T' + String(asset.relocate.completesTurn),
      tone: 'warn',
    });
  }
  if (asset.flags.length > 0) rows.push({ key: 'FLAGS', value: asset.flags.join(' · ') });
  return [...rows, ...asset.effects.map(effectRow)];
}

/**
 * The key/value block for a contact.
 *
 * Every field here is on `ContactView` and therefore on a phantom too. Nothing
 * reads `friendly[]`, nothing reads a true id, and nothing is conditional on
 * anything a phantom lacks — a row built from this function is identical for a
 * phantom and a real contact at the same rung, modulo the alias (must-not #5).
 */
export function contactRows(contact: ContactViewWire): readonly DetailRow[] {
  const rows: DetailRow[] = [
    { key: 'TYPE', value: contact.presentedType },
    { key: 'LOCATION', value: contactLocationWords(contact) },
    { key: 'CHARACTERIZED', value: contact.characterized ? 'YES' : 'NO' },
  ];
  const believed = contact.believedStatus;
  if (believed !== null) {
    const value =
      believed.status +
      (believed.certain ? '' : ' · UNCERTAIN') +
      ' · AS OF T' +
      String(believed.asOfTurn);
    rows.push(believed.certain ? { key: 'STATUS', value } : { key: 'STATUS', value, tone: 'warn' });
  }
  if (contact.expiresTurn !== null) {
    rows.push({ key: 'FIX EXPIRES', value: 'T' + String(contact.expiresTurn), tone: 'warn' });
  }
  if (contact.publishedBy !== null) rows.push({ key: 'PUBLISHED BY', value: contact.publishedBy });
  return rows;
}

/**
 * The key/value block for a region.
 *
 * `ADJACENT` carries ids and no number. Range is a separate list with its own
 * heading, from this region as the origin — the two are never the same row
 * (must-not #6).
 */
export function regionRows(view: CellViewWire, region: RegionViewWire): readonly DetailRow[] {
  const own = view.friendly.filter(
    (asset) => asset.location.kind === 'GROUND' && asset.location.region === region.id,
  ).length;
  const contacts = view.contacts.filter(
    (contact) => contact.location.kind === 'REGION' && contact.location.region === region.id,
  ).length;
  const adjacent = adjacencyOf(view, region.id);
  return [
    { key: 'FRIENDLY', value: String(own) },
    { key: 'CONTACTS', value: String(contacts) },
    { key: 'ADJACENT', value: adjacent.length === 0 ? 'NONE' : adjacent.join(' · ') },
  ];
}

/**
 * The key/value block for an orbital band (Phase 6).
 *
 * The same shape as `regionRows` and for the same reason — a band is a zone, and
 * the rail asks a zone the same three questions. What it adds is the two counts
 * a ring cannot carry: how many of the band's contacts the wire has *placed*, and
 * how many it has not. `SLOTS` is `map.bandSlots`, which is also what divides the
 * ring, so the number the operator counts round the canvas and the number the
 * rail states are one value.
 *
 * Debris is **not** here: it is `EnvironmentBlock`'s, drawn as a level and a
 * five-pip meter beside the weather, which is where every other environmental
 * number in this console already lives.
 */
export function bandDetailRows(
  view: CellViewWire,
  band: string,
  slots: number,
): readonly DetailRow[] {
  const own = view.friendly.filter(
    (asset) => asset.location.kind === 'ORBIT' && asset.location.band === band,
  ).length;
  const inbound = view.friendly.filter(
    (asset) => asset.location.kind === 'TRANSIT' && asset.location.to === band,
  ).length;
  const held = view.contacts.filter(
    (contact) =>
      (contact.location.kind === 'BAND' || contact.location.kind === 'SLOT') &&
      contact.location.band === band,
  );
  const unlocated = held.filter((contact) => contact.location.kind === 'BAND').length;

  const rows: DetailRow[] = [
    { key: 'SLOTS', value: String(slots) },
    { key: 'FRIENDLY', value: String(own) },
  ];
  if (inbound > 0) rows.push({ key: 'INBOUND', value: String(inbound) });
  rows.push({ key: 'CONTACTS', value: String(held.length) });
  if (unlocated > 0) {
    rows.push({ key: 'SLOT UNKNOWN', value: String(unlocated), tone: 'warn' });
  }
  return rows;
}

/**
 * The rail's rows for a cyber enclave — and the **one place** the board states a
 * network's posture.
 *
 * Must-not #2 in one function: `hardening` is on the wire three times and it is
 * said here, on the network, and never stamped onto the fourteen assets that
 * hang off it. Each asset's own posture is a different fact and lives on its own
 * card's footing tick.
 *
 * The two `null`s are both drawn as themselves. A router the projection would
 * not name reads `ROUTER UNKNOWN` and not blank; a posture nobody has assessed
 * reads `UNASSESSED` and not NORMAL — and both carry the `warn` tone, because an
 * enclave whose way in you cannot name is a thing to act on rather than a field
 * that happens to be empty.
 */
export function networkDetailRows(
  view: CellViewWire,
  network: CyberNetworkViewWire,
): readonly DetailRow[] {
  const row = networkRows(view).find((candidate) => candidate.id === network.id);
  const rows: DetailRow[] = [
    { key: 'SIDE', value: network.side === view.viewer.team ? 'FRIENDLY' : 'ADVERSARY' },
    {
      key: 'ROUTER',
      value: network.router ?? ROUTER_UNKNOWN,
      ...(network.router === null ? { tone: 'warn' as const } : {}),
    },
    {
      key: 'POSTURE',
      value: network.hardening ?? HARDENING_UNKNOWN,
      ...(network.hardening === null ? { tone: 'warn' as const } : {}),
    },
  ];
  if (row === undefined) return rows;
  rows.push({ key: 'HOSTS', value: String(row.hosts) });
  // Access is the rung on the way in, and on one's own enclave it is the thing
  // the wire deliberately does not say (RB §6.2). `UNASSESSED` there is not a
  // gap in the projection — it is the projection refusing to tell a defender
  // whether they are compromised.
  rows.push({
    key: 'ACCESS',
    value: row.access === ACCESS_UNKNOWN ? 'UNASSESSED' : row.access,
    ...(accessRung(row.access) >= 1 ? { tone: 'red' as const } : {}),
  });
  if (row.foothold) rows.push({ key: 'FOOTHOLD', value: 'HELD' });
  if (row.isolated.length > 0) {
    rows.push({ key: 'ISOLATED', value: row.isolated.join(' · '), tone: 'warn' });
  }
  return rows;
}

/**
 * The rail's rows for a machine known only to cyber — five fields, because the
 * wire carries five.
 *
 * The absences are the content. There is no rung, because nothing detected it;
 * no location, because a foothold tells you which enclave and not which sky; no
 * type, because nothing characterised it. A panel that filled any of those in
 * would be inventing the intelligence the foothold did not buy, and the reason
 * this is a separate arm from `contactRows` is that `contactRows` has all three
 * and would have to learn to omit them.
 */
export function cyberContactRows(contact: CyberContactViewWire): readonly DetailRow[] {
  return [
    { key: 'ENCLAVE', value: contact.network },
    {
      key: 'ACCESS',
      value: contact.access,
      ...(accessRung(contact.access) >= 1 ? { tone: 'warn' as const } : {}),
    },
    {
      key: 'POSTURE',
      value: contact.hardening ?? HARDENING_UNKNOWN,
      ...(contact.hardening === null ? { tone: 'warn' as const } : {}),
    },
    { key: 'PERSISTENCE', value: contact.persistence ? 'ESTABLISHED' : 'NONE' },
    // The sentence the whole arm exists for. RB §5.6.2: a foothold makes an asset
    // "visible (to cyber; not FOUND)", and an operator reading a panel with no
    // rung on it needs to know that is the rule rather than a missing field.
    { key: 'DETECTION', value: 'NOT FOUND — CYBER ONLY', tone: 'warn' },
  ];
}

// ---------------------------------------------------------------------------
// Active orders on the selection
// ---------------------------------------------------------------------------

export interface ActiveOrder {
  /** The queue key, so EDIT and cancel act on a row and not on a position. */
  readonly key: string;
  readonly mission: string;
  readonly counterpart: string;
}

export function targetWords(target: OrderTargetWire): string {
  switch (target.kind) {
    case 'NONE':
      return '';
    case 'SELF':
      return 'SELF';
    case 'CONTACT':
      return target.contact;
    case 'OWN_ASSET':
      return target.asset;
    case 'REGION':
      return target.region;
    case 'BAND':
      return target.band;
    case 'SLOT':
      return target.band + ' SLOT ' + String(target.slot);
    case 'MW_EVENT':
      return target.event;
  }
}

/**
 * Orders standing against this selection this turn (design 1357–1382).
 *
 * Driven by the **queue** rather than by `orders.state.own.accepted`, which is
 * what Phase 2 had. Two reasons, and the second is the load-bearing one:
 *
 *  - the queue is the whole set the seat wants (`zOrdersDraftPayload` is never a
 *    delta), and it converges on `accepted` on the round trip — so it says the
 *    same thing a beat earlier;
 *  - EDIT and CANCEL need something to act *on*, and an accepted order carries no
 *    identity the client can hold. The queue key does.
 */
export function ordersFor(
  queue: readonly QueuedOrder[],
  subjectId: string,
): readonly ActiveOrder[] {
  return queue
    .filter(
      (entry) => entry.order.asset === subjectId || targetWords(entry.order.target) === subjectId,
    )
    .map((entry) => ({
      key: entry.key,
      mission: missionLabel(entry.order.mission),
      counterpart:
        entry.order.asset === subjectId ? targetWords(entry.order.target) : entry.order.asset,
    }));
}

// ---------------------------------------------------------------------------
// The launch event's detail rows
// ---------------------------------------------------------------------------

/**
 * A track's header rows, and **not** its observations.
 *
 * The observations are a staged list with two distinct absences in it and they
 * get their own block (`mw/ObservationBlock.tsx`). What belongs here is what is
 * true of the *track*: how far the reveal has got, what the seat has asserted,
 * what a decision now would earn, and — once decided — what it did earn.
 *
 * `credit` is `mwStageCredit` and nothing else. There is no correctness term in
 * this list because there is none on the wire (M4-D7), and a row reading `TOTAL`
 * would be the client inventing the number the whole decision withholds.
 */
export function mwTrackRows(track: MwTrackWire): readonly DetailRow[] {
  const rows: DetailRow[] = [
    // The cursor, and only the cursor. "No data yet" is the observation block's
    // sentence and saying it twice on one screen is the copy rules' first cut.
    {
      key: 'STAGE',
      value: String(track.revealedThroughStage) + ' / 4',
      ...(track.revealedThroughStage === 0 ? { tone: 'warn' as const } : {}),
    },
  ];

  if (track.release === null) {
    rows.push({
      key: 'CREDIT NOW',
      value: signed(creditAt(track.revealedThroughStage)),
      ...(creditAt(track.revealedThroughStage) === 0 ? { tone: 'warn' as const } : {}),
    });
    if (track.workingIdentity !== null) {
      rows.push({ key: 'WORKING', value: track.workingIdentity });
    }
  } else {
    // The working identity first: on a briefed track the release carries the
    // disposition (`BENIGN`) rather than the model, and the model is what the
    // operator names out loud.
    rows.push({ key: 'CALLED', value: track.workingIdentity ?? track.release.identity });
    rows.push({ key: 'AT STAGE', value: String(track.release.stage) });
    // A briefed track was never the seat's decision (M4-D3), so it has no stage
    // term. A computed `0` there is a number standing in for one that does not
    // exist, and it reads as a score.
    rows.push({
      key: 'CREDIT',
      value: track.briefed ? '—' : signed(creditAt(track.release.stage)),
    });
  }

  // Where the launch is *going*, which is the one observation that is a fact
  // about the seat's own theatre rather than about the type. The origin is not
  // repeated here: the observation block below carries it with the stage that
  // produced it, and the copy rules say a thing once per screen.
  const impact = observationFor(track, 'impactSite');
  if (impact !== undefined) {
    rows.push({
      key: observableLabel('impactSite'),
      // A dropped data point is drawn where a value would be, and says so.
      value: impact.value ?? 'NOT RECEIVED',
      ...(impact.value === null ? { tone: 'warn' as const } : {}),
    });
  }

  return rows;
}

/** The chip beside a track's id: what has happened to it, in one word. */
export function mwTrackChip(track: MwTrackWire): {
  readonly text: string;
  readonly colour: string;
} {
  const state = trackStateOf(track);
  if (state === 'RELEASED') return { text: 'RELEASED', colour: 'var(--red-text)' };
  if (state === 'DISMISSED') return { text: 'DISMISSED', colour: 'var(--text-muted)' };
  return { text: track.open ? 'OWES A DECISION' : 'LIVE', colour: 'var(--warn)' };
}
